import { useEffect } from 'react';
import { getRoute, useNavigate } from '../../../../routes';
import { SurveyStatus, useGetSurveyStatus } from '../../core/hooks/useGetSurveyStatus';

type UseSurveyEligibilityRedirectOptions = {
  /**
   * Required survey status for the page. If the user's survey status
   * doesn't match, they'll be redirected to the survey home page.
   * Only 'Incomplete' and 'Completed' are valid for page access.
   * @default 'Incomplete'
   */
  requiredStatus?: Extract<SurveyStatus, 'Incomplete' | 'Completed'>;
};

type UseSurveyEligibilityRedirectReturn = {
  /** Whether the user has access to the survey page (not loading and user is eligible) */
  hasAccess: boolean;
};

/**
 * Hook that checks survey eligibility and redirects to survey home if requirements aren't met.
 * Useful for survey pages that should only be accessible to users with specific survey statuses.
 */
export const useSurveyEligibilityRedirect = (
  options: UseSurveyEligibilityRedirectOptions = {},
): UseSurveyEligibilityRedirectReturn => {
  const { requiredStatus = 'Incomplete' } = options;
  const navigate = useNavigate();
  const surveyStatusResult = useGetSurveyStatus();

  useEffect(() => {
    if (surveyStatusResult.loading) {
      return;
    }

    if (!surveyStatusResult.success) {
      // On error, redirect to survey home to show error state
      navigate(getRoute('surveyHome', {}), { replace: true });
      return;
    }

    const { survey } = surveyStatusResult;

    // Check if user meets the required status
    if (survey.status !== requiredStatus) {
      navigate(getRoute('surveyHome', {}), { replace: true });
    }
  }, [navigate, surveyStatusResult, requiredStatus]);

  const hasAccess =
    !surveyStatusResult.loading &&
    surveyStatusResult.success &&
    surveyStatusResult.survey.status === requiredStatus;

  return {
    hasAccess,
  };
};
